/**
 * useFirebaseAutoSave - Hook for automatic tournament saving to Firestore
 * Saves tournament state to Firebase when changes occur (debounced)
 */
import { useEffect, useRef, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import firebaseSyncService from '../services/firebaseSync';

const useFirebaseAutoSave = (tournamentState, enabled = true) => {
  const { isSignedIn, getCurrentUser } = useAuth();
  const { showError } = useToast();
  const timeoutRef = useRef(null);
  const previousStateRef = useRef(null);
  const [isSaving, setIsSaving] = useState(false);
  const [lastSaveTime, setLastSaveTime] = useState(null);

  useEffect(() => {
    if (!enabled || !isSignedIn()) {
      return;
    }

    const user = getCurrentUser();
    if (!user) return;

    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    const currentStateStr = JSON.stringify(tournamentState);
    if (previousStateRef.current === currentStateStr) {
      return;
    }

    previousStateRef.current = currentStateStr;

    timeoutRef.current = setTimeout(async () => {
      setIsSaving(true);
      try {
        if (!firebaseSyncService.currentTournamentId) {
          const tournamentId = await firebaseSyncService.createNewTournament(
            tournamentState,
            user.email,
            null
          );
          if (tournamentId) {
            console.log('✅ New tournament created:', tournamentId);
          }
        } else {
          await firebaseSyncService.saveTournament(
            tournamentState,
            user.email,
            'auto-save'
          );
          console.log('💾 Auto-saved tournament to Firestore');
        }
        setLastSaveTime(new Date());
      } catch (error) {
        console.error('❌ Auto-save error:', error);
        showError('Erreur de sauvegarde automatique');
      } finally {
        setIsSaving(false);
      }
    }, 2000);

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [tournamentState, enabled, isSignedIn, getCurrentUser, showError]);

  return { isSaving, lastSaveTime };
};

export default useFirebaseAutoSave;
